import axios from 'axios';
import { toBase64, uploadFile } from './attachment_helper';
import { 
    getSubmissionStatus, 
    getDraftStatus, 
    isValidStatusTransition, 
    validateApplicationForStatus,
    validateConcurrentChanges,
    validateAndCorrectApplicationStatus,
    APPLICATION_STATUS 
} from '../statusManager';

const API_URL = process.env.REACT_APP_ESPO_API_URL+'/MMApplicationNew';
const API_KEY = process.env.REACT_APP_ESPO_API_KEY;

// Fetch application data
export const fetchApplication = async (applicationId) => {
    try {
        const response = await axios.get(`${API_URL}/${applicationId}`, {
            headers: {
                'x-api-key': API_KEY,
            }
        });
        console.log("fetchApplication Response - ", response);
        return response; // Return only the data
    } catch (error) {
        console.error('Failed to fetch application data:', error);
        throw new Error('Failed to fetch application data');
    }
};

// Update application data with status validation
export const updateApplication = async (applicationId, formData, options = {}) => {
    const { isSubmission = false, isDraft = false, skipStatusValidation = false } = options;
    
    try {
        // First, fetch current application data to check for concurrent changes
        const currentApplicationResponse = await fetchApplication(applicationId);
        const currentApplicationData = currentApplicationResponse;
        
        // Check for concurrent status changes if not skipping validation
        if (!skipStatusValidation && formData.status && currentApplicationData.status) {
            const concurrentValidation = validateConcurrentChanges(
                { status: formData.originalStatus || formData.status }, 
                currentApplicationData
            );
            
            if (concurrentValidation.hasConflict) {
                throw new Error(concurrentValidation.message);
            }
        }
        
        let finalFormData = { ...formData };
        
        // Determine the correct status based on the operation
        if (isSubmission) {
            // Check if signed form is already uploaded
            const hasSignedForm = currentApplicationData.applicationSignedFormId || 
                                 formData.applicationSignedFormId;
            
            const targetStatus = getSubmissionStatus(currentApplicationData, hasSignedForm);
            
            // Validate application for target status
            const validation = validateApplicationForStatus(currentApplicationData, targetStatus);
            if (!validation.isValid) {
                throw new Error(validation.errors.join(', '));
            }
            
            finalFormData.status = targetStatus;
        } else if (isDraft) {
            finalFormData.status = getDraftStatus(currentApplicationData);
        }
        
        // Validate status transition if status is being changed
        if (finalFormData.status && currentApplicationData.status && 
            finalFormData.status !== currentApplicationData.status && !skipStatusValidation) {
            
            if (!isValidStatusTransition(currentApplicationData.status, finalFormData.status)) {
                throw new Error(
                    `Invalid status transition from "${currentApplicationData.status}" to "${finalFormData.status}"`
                );
            }
        }
        
        const cleanedData = cleanFormData(finalFormData);
        console.log("update form data - ", cleanedData);
        
        const response = await axios.put(`${API_URL}/${applicationId}`, cleanedData, {
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json',
            }
        });
        
        console.log("updateApplication Response - ", response);
        return response;
    } catch (error) {
        console.error('Failed to update application:', error);
        throw new Error(`Failed to update application: ${error.message}`);
    }
};

// Create a new application
export const createApplication = async (newApplicationData) => {
    //const cleanedData = cleanFormData(newApplicationData); // Clean the newApplicationData
    try {
        // Ensure new applications start with Draft status
        const applicationData = {
            ...newApplicationData,
            status: APPLICATION_STATUS.DRAFT
        };
        
        const response = await axios.post(API_URL, applicationData, {
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json',
            }
        });
        console.log("createApplication Response - ", response);
        return response;
    } catch (error) {
        console.error('Failed to create application:', error);
        throw new Error('Failed to create application');
    }
};

// Check if application has signed form uploaded
export const hasSignedFormUploaded = async (applicationId) => {
    try {
        const application = await fetchApplication(applicationId);
        return !!(application.applicationSignedFormId);
    } catch (error) {
        console.error('Failed to check signed form status:', error);
        return false;
    }
};

// Get application with status validation and auto-correction
export const getApplicationWithStatusValidation = async (applicationId) => {
    try {
        const application = await fetchApplication(applicationId);
        
        // Validate and potentially correct the application status
        const statusValidation = validateAndCorrectApplicationStatus(application);
        
        let finalApplication = application;
        
        // If status needs correction, update it in the background
        if (statusValidation.needsCorrection) {
            console.log(`Auto-correcting application status: ${statusValidation.reason}`);
            console.log(`Changing status from "${statusValidation.originalStatus}" to "${statusValidation.correctedStatus}"`);
            
            try {
                // Update the status in the background
                await updateApplication(applicationId, {
                    status: statusValidation.correctedStatus
                }, { 
                    skipStatusValidation: true // Skip validation since we're doing the correction
                });
                
                // Update the local application object with corrected status immediately
                finalApplication = {
                    ...application,
                    status: statusValidation.correctedStatus
                };
                
                console.log('Application status corrected successfully');
            } catch (correctionError) {
                console.error('Failed to auto-correct application status:', correctionError);
                // Continue with original status if correction fails
                finalApplication = application;
            }
        }
        
        // Add computed properties for UI based on the final (potentially corrected) status
        const enhancedApplication = {
            ...finalApplication,
            hasSignedForm: !!(finalApplication.applicationSignedFormId),
            canEdit: [APPLICATION_STATUS.DRAFT, APPLICATION_STATUS.MISSING_INFORMATION].includes(finalApplication.status),
            canSubmit: [APPLICATION_STATUS.DRAFT, APPLICATION_STATUS.MISSING_INFORMATION].includes(finalApplication.status),
            canUploadSignedForm: [APPLICATION_STATUS.PENDING_SIGNED_FORM, APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM].includes(finalApplication.status),
            isReadOnly: ![APPLICATION_STATUS.DRAFT, APPLICATION_STATUS.MISSING_INFORMATION].includes(finalApplication.status),
            statusCorrected: statusValidation.needsCorrection,
            originalStatus: statusValidation.originalStatus
        };
        
        return enhancedApplication;
    } catch (error) {
        console.error('Failed to fetch application with status validation:', error);
        throw error;
    }
};

// Upload signed application form with enhanced status management
export const uploadSignedApplication = async (applicationId, signedFormFile) => {
    console.log('uploadSignedApplication called with:', {
        applicationId,
        fileName: signedFormFile?.name,
        fileSize: signedFormFile?.size
    });
    
    try {
        // First, get the current application status with auto-correction
        const currentApplicationData = await getApplicationWithStatusValidation(applicationId);
        
        console.log('Current application data after status validation:', {
            id: applicationId,
            status: currentApplicationData.status,
            hasSignedForm: currentApplicationData.hasSignedForm,
            statusCorrected: currentApplicationData.statusCorrected
        });
        
        // Check if upload is allowed for current status
        const allowedUploadStatuses = [
            APPLICATION_STATUS.PENDING_SIGNED_FORM,
            APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM
        ];
        
        if (!allowedUploadStatuses.includes(currentApplicationData.status)) {
            throw new Error(
                `Cannot upload signed form. Current status: ${currentApplicationData.status}. ` +
                `Upload is only allowed for: ${allowedUploadStatuses.join(', ')}`
            );
        }
        
        // Step 1: Prepare the file payload using the same structure as other uploads
        const base64File = await toBase64(signedFormFile);
        const filePayload = {
            name: signedFormFile.name,
            type: signedFormFile.type,
            role: "Attachment",
            relatedType: "MMApplicationNew",
            relatedId: applicationId,
            field: "applicationSignedForm",
            file: base64File,
        };

        console.log('Uploading signed form with payload:', filePayload);

        // Step 2: Upload the file using the existing uploadFile helper
        const response = await uploadFile(filePayload);

        console.log('File upload response:', response);

        // Step 3: Update application status to "Submitted" after successful upload
        // But first check if it's already "Submitted" (in case auto-correction happened)
        const finalApplicationData = await fetchApplication(applicationId);
        
        if (finalApplicationData.status === APPLICATION_STATUS.SUBMITTED) {
            console.log('Application already in Submitted status, no update needed');
            return {
                success: true,
                message: 'Signed application uploaded successfully',
                attachmentId: response.id,
                newStatus: APPLICATION_STATUS.SUBMITTED
            };
        }
        
        const statusUpdatePayload = {
            status: APPLICATION_STATUS.SUBMITTED,
            applicationSignedFormId: response.id, // Store the attachment ID
            originalStatus: currentApplicationData.status // Include original status for validation
        };

        console.log('Updating application status to Submitted');

        // Use a direct update approach that bypasses all validations for upload
        try {
            const cleanedData = cleanFormData(statusUpdatePayload);
            console.log("Direct update form data - ", cleanedData);
            
            const statusUpdateResponse = await axios.put(`${API_URL}/${applicationId}`, cleanedData, {
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json',
                }
            });
            
            console.log('Status update response:', statusUpdateResponse);
        } catch (updateError) {
            console.error('Failed to update status after upload:', updateError);
            // If direct update fails, try with our helper function
            const statusUpdateResponse = await updateApplication(
                applicationId, 
                statusUpdatePayload, 
                { skipStatusValidation: true }
            );
            console.log('Fallback status update response:', statusUpdateResponse);
        }

        return {
            success: true,
            message: 'Signed application uploaded successfully',
            attachmentId: response.id,
            newStatus: APPLICATION_STATUS.SUBMITTED
        };

    } catch (error) {
        console.error('Failed to upload signed application:', error);
        if (error.response) {
            // Server responded with error status
            throw new Error(`Upload failed: ${error.response.data?.message || error.response.statusText}`);
        } else if (error.request) {
            // Request was made but no response received
            throw new Error('Upload failed: No response from server');
        } else {
            // Something happened in setting up the request
            throw new Error(`Upload failed: ${error.message}`);
        }
    }
};

// Auto-correct application status if inconsistent with signed form presence
export const autoCorrectApplicationStatus = async (applicationId) => {
    try {
        const application = await fetchApplication(applicationId);
        const statusValidation = validateAndCorrectApplicationStatus(application);
        
        if (statusValidation.needsCorrection) {
            console.log(`Auto-correcting application ${applicationId}: ${statusValidation.reason}`);
            
            await updateApplication(applicationId, {
                status: statusValidation.correctedStatus
            }, { 
                skipStatusValidation: true 
            });
            
            return {
                corrected: true,
                originalStatus: statusValidation.originalStatus,
                newStatus: statusValidation.correctedStatus,
                reason: statusValidation.reason
            };
        }
        
        return {
            corrected: false,
            status: application.status,
            reason: 'No correction needed'
        };
    } catch (error) {
        console.error(`Failed to auto-correct application ${applicationId}:`, error);
        throw error;
    }
};

// Force refresh application status and return updated data
export const refreshApplicationStatus = async (applicationId) => {
    try {
        // First run auto-correction
        const correctionResult = await autoCorrectApplicationStatus(applicationId);
        
        // Then fetch the latest data
        const latestApplication = await fetchApplication(applicationId);
        
        return {
            ...latestApplication,
            statusCorrected: correctionResult.corrected,
            originalStatus: correctionResult.originalStatus || latestApplication.status
        };
    } catch (error) {
        console.error(`Failed to refresh application status ${applicationId}:`, error);
        throw error;
    }
};

// Clean the formData
const cleanFormData = (data) => {
    if (Array.isArray(data)) {
        return data.map(cleanFormData).filter(item => item !== null && item !== undefined);
    } else if (data && typeof data === 'object') {
        return Object.keys(data).reduce((acc, key) => {
            const value = cleanFormData(data[key]);
            if (value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});
    }
    return data;
};
